const jobErrors: Record<string, { title: string; description: string }> = {
  NO_SELLABLE_SKUS: { title: 'SKU 不可售', description: '所选 SKU 均被判定为不可售' },
  NO_SKU_CANDIDATES: { title: '无符合条件的 SKU', description: '没有符合颜色、尺码筛选条件的 SKU' },
  SHOP_BINDING_ARCHIVED: { title: '旧任务已封存', description: '旧店铺已解绑，任务、已完成步骤和平台回执已保留。重新授权同一 WB 卖家后，后台会分批检查恢复条件。' },
  SHOP_BINDING_SUPERSEDED: { title: '新绑定的发布优先', description: '同源商品已在新绑定发布；旧任务继续封存，避免重复发布。请查看新绑定的任务结果。' },
  WB_CARD_OTHER_BINDING_PENDING: { title: '等待同卖家原请求确认', description: '另一店铺绑定正在确认此货号。保留原请求，等待确认后继续，避免重复建卡。' },
  WB_CARD_OTHER_BINDING_EXISTS: { title: '同卖家已有此货号', description: '另一店铺绑定已经发布此货号。请核对原商品卡，不要重复创建。' },
  WB_RETRY_EXHAUSTED: { title: 'WB 请求重试已用尽', description: '本步骤的 WB 请求多次未成功。展开原始反馈查看网络、平台或业务原因；处理后重试未完成步骤。' },
  AI_RETRY_EXHAUSTED: { title: 'AI 处理重试已用尽', description: '当前内容处理多次未成功。展开原始反馈检查超时、服务配置或返回内容；处理后重试本步骤。' },
  WB_NETWORK_WAIT: { title: '等待 WB 网络恢复', description: 'WB 连接暂不可用，系统会按下次执行时间自动继续原步骤。已完成的内容、商品卡和回执保留。' },
  WB_CARD_CONFIRMATION_TIMEOUT: { title: '原商品卡需人工核对', description: '多轮核对后仍未完整确认原建卡结果，已停止自动重试。原回执、商品卡身份和额度占用保留；确认超时不代表未建卡。请核对原卡后通过原任务继续，系统不会直接重新建卡。' },
  WB_CARD_CONFIRMATION_REQUIRES_REVIEW: { title: '请先核对原商品卡', description: '此任务不能直接批量重试。请核对原建卡回执，再通过原任务显式确认并继续；原始回执和额度占用保留。' },
  AI_AUTH_INVALID: { title: 'AI 授权或访问被拒绝', description: '请核对该任务实际使用的 AI 服务地址、密钥及访问权限；更新对应授权后恢复原任务。' },
  AI_SERVICE_WAIT: { title: 'AI 服务暂不可用', description: '服务超时或暂时无法响应。请查看任务状态和下次执行时间，原步骤与已完成结果会保留。' },
  AI_PROVIDER_LIMIT_WAIT: { title: '等待 AI 服务额度恢复', description: 'AI 服务返回限流或额度限制，系统保留服务商给出的等待时间，再继续原步骤。' },
  AI_PAYLOAD_TOO_LARGE: { title: 'AI 请求内容超过服务商限制', description: '请核对原始反馈和服务商请求大小限制；这类内容错误保留有限重试次数。' },
  WB_INVENTORY_CARD_UNAVAILABLE: { title: 'WB 暂未返回原商品卡', description: '原卡回执已保留。请在 WB 核对原卡和仓库，原卡可读取后仅重试库存步骤。' },
  WB_INVENTORY_CARD_TRASHED: { title: 'WB 原商品卡在回收站', description: '请先在 WB 恢复原卡，再仅重试库存步骤；系统不会重新建卡。' },
  WB_INVENTORY_RECOVERY_CHECK: { title: '正在核对原卡库存回执', description: '保留原仓库、库存数量及已完成步骤，后台正在核对原卡。' },
  WB_INVENTORY_PROPAGATION_WAIT: { title: '等待 WB 同步新卡', description: 'WB 库存服务尚未确认新卡，后台稍后仅重试库存步骤。' },
  WB_INVENTORY_IDENTITY_CHANGED: { title: '原商品卡身份需要核对', description: '原货号、商品卡或条码与当前回执不一致。已保留原回执，请核对 WB 原卡后仅重试库存。' },
  WB_INVENTORY_RECEIPT_CONFLICT: { title: '原卡回执已变化', description: '已保留当前回执，请仅重试库存步骤重新核对。' },
};

export type JobErrorPresentation = {
  title: string;
  description: string;
  guidance?: string;
  technicalDetails?: string;
};

function aiErrorPresentation(code: string, message: string): JobErrorPresentation | null {
  if (!code.startsWith('AI_')) return null;
  const status = Number(message.match(/\bAI(?:_[A-Z0-9]+)* provider returned HTTP (\d{3})\b/iu)?.[1]) || null;
  const categorized = ['AI_AUTH_INVALID', 'AI_PROVIDER_LIMIT_WAIT', 'AI_PAYLOAD_TOO_LARGE', 'AI_SERVICE_WAIT'].includes(code);
  const present = (title: string, description: string, guidance: string): JobErrorPresentation => ({ title, description, guidance });
  const authGuidance = '请核对该任务实际使用的 AI 服务地址、密钥和模型访问权限，保存正确配置后恢复原任务。';
  if (code === 'AI_IMAGE_REFERENCE_UNAVAILABLE') {
    return present('参考图片无法读取或处理', '当前步骤未能准备可用的商品参考图片，具体原因请查看任务反馈。',
      '请检查商品原图是否可访问及图片格式，再重试当前步骤；已完成步骤会保留。');
  }
  if (code === 'AI_SERVICE_WAIT' && message === '参考图片暂时无法读取，等待恢复') {
    return present('参考图片暂时无法读取', '下载参考图片时遇到临时故障，本次尚未向 AI 发起处理请求。',
      '系统保留已完成步骤，按下次执行时间自动重试参考图片。');
  }
  if (code === 'AI_PAYLOAD_TOO_LARGE' || !categorized && status === 413) {
    return present('AI 请求内容超过服务商限制', 'AI 服务商拒绝了过大的请求正文或图片（HTTP 413）。',
      '请检查参考图片、输入内容及服务商请求大小限制，再重试本步骤。');
  }
  if (code === 'AI_AUTH_INVALID' || !categorized && (status === 401 || status === 403)) {
    if (/\binvalid[_ -](?:api[_ -])?key\b/iu.test(message))
      return present('AI 服务返回密钥无效', 'AI 服务商明确表示本次使用的密钥无效，当前授权未通过。', authGuidance);
    if (status === 403)
      return present('AI 服务拒绝访问', 'AI 服务端拒绝当前请求（HTTP 403），需要核对账号和模型的访问权限。', authGuidance);
    if (status === 401)
      return present('AI 授权未通过', 'AI 服务未接受本次授权（HTTP 401），具体原因以原始反馈为准。', authGuidance);
    return present('AI 授权或访问被拒绝', 'AI 服务商拒绝了当前授权或访问，需要核对该任务使用的服务配置。', authGuidance);
  }
  if (code === 'AI_PROVIDER_LIMIT_WAIT' || !categorized && (code.endsWith('_RATE_LIMITED') || status === 429
    || /\b(?:Error code|HTTP|status)\s*[:= -]?\s*429\b/iu.test(message))) {
    const waiting = code === 'AI_PROVIDER_LIMIT_WAIT';
    if (/\bfiles?(?:[ _-]+(?:processing|upload|storage))?[ _-]+(?:rate[ _-]+)?(?:limit|quota)\b/iu.test(message))
      return present('AI 文件处理额度受限', `AI 服务商限制了文件处理或上传，${waiting ? '系统保留服务商给出的等待时间。' : '需按服务商反馈的恢复时间处理。'}`,
        waiting ? '请查看下次执行时间；也可联系 AI 服务商核对该账号的文件处理限制。' : '达到服务商允许恢复的时间后重试原步骤；也可联系 AI 服务商核对限制。');
    if (/\b(?:insufficient[_ -](?:quota|credit|balance)|billing[_ -]hard[_ -]limit|quota[_ -]exceeded)\b|余额不足|配额不足/iu.test(message))
      return present('AI 服务商可用额度不足', 'AI 服务商明确返回了账号额度限制，需核对该任务所用账号的可用额度。',
        '请核对对应账号额度或套餐；更新可用配置后继续原任务。');
    return present('AI 服务请求受限', 'AI 服务商返回了限流或额度限制，目前未能确定更细的限制原因。',
      waiting ? '请查看下次执行时间，系统按已保存的等待时间继续原步骤。' : '请按服务商反馈的限制处理，再重试原步骤。');
  }
  if (code === 'AI_SERVICE_WAIT' || !categorized && (code === 'AI_TIMEOUT' || code === 'AI_IMAGE_TIMEOUT'
    || code.endsWith('_UNAVAILABLE') || status !== null && status >= 500)) {
    const resume = code === 'AI_SERVICE_WAIT' ? '系统保留原步骤，按下次执行时间继续；持续发生时请联系管理员检查服务和网络。'
      : '请检查对应服务和网络，恢复后重试原步骤。';
    if (/\b(?:CERT_HAS_EXPIRED|CERT_NOT_YET_VALID|UNABLE_TO_VERIFY_LEAF_SIGNATURE|ERR_TLS_CERT_ALTNAME_INVALID|DEPTH_ZERO_SELF_SIGNED_CERT|SELF_SIGNED_CERT_IN_CHAIN)\b/iu.test(message))
      return present('AI 连接证书校验失败', /\bCERT_HAS_EXPIRED\b/u.test(message) ? 'AI 连接使用的证书已过期，证书校验未通过。'
        : '连接 AI 服务时证书校验未通过，本次通信未正常完成。', '请管理员核对 AI 服务地址、证书链和服务器时间。');
    if (/\b(?:UND_ERR_CONNECT_TIMEOUT|ETIMEDOUT)\b/u.test(message))
      return present('连接 AI 服务超时', '连接或网络请求未在限定时间内完成，尚不能据此判断密钥失效。', resume);
    if (/\b(?:ECONNRESET|UND_ERR_SOCKET)\b/u.test(message))
      return present('AI 连接中断', '本次通信连接中途关闭，未能正常完成 AI 请求。', resume);
    if (/\b(?:EAI_AGAIN|ENOTFOUND)\b/u.test(message))
      return present('AI 服务地址解析失败', '服务器未能解析配置的 AI 服务域名。', '请管理员检查 AI 服务地址及服务器 DNS；恢复后继续原步骤。');
    if (/\btoo many (?:connections|clients)|connection pool (?:exhausted|is full)|remaining connection slots/iu.test(message))
      return present('AI 服务内部连接资源不足', 'AI 服务商返回的错误说明其内部连接资源不足，当前请求未完成。', resume);
    if (/\brequest timed out\b/iu.test(message) || code === 'AI_TIMEOUT' || code === 'AI_IMAGE_TIMEOUT')
      return present('AI 处理超时', '本次 AI 处理未在限定时间内完成，原步骤和已完成结果会保留。', resume);
    if (status !== null && status >= 500 && /\bsystem[_ ]cpu[_ ]overloaded\b/iu.test(message))
      return present('AI 服务过载', 'AI 服务端明确返回 CPU 过载，当前请求未完成。', resume);
    if (status !== null && status >= 500)
      return present(`AI 服务暂时异常（HTTP ${status}）`, 'AI 服务端或其上游返回异常，需要等待服务恢复。', resume);
  }
  if (code === 'AI_RESPONSE_INVALID')
    return present('AI 服务返回格式异常', 'AI 服务已响应，但响应体为空或无法解析为要求的 JSON 格式。',
      '请核对该服务的接口兼容性和原始反馈，再重试当前步骤。');
  if (code === 'AI_OUTPUT_INVALID')
    return present('AI 返回内容不符合要求', '当前步骤未取得符合要求的结构化内容，可能涉及字段缺失、取值或格式问题。',
      '请查看原始反馈中的具体字段；系统仍保留有限的内容重试次数。');
  return null;
}

export function jobErrorPresentation(code: string | null | undefined, message?: string | null): JobErrorPresentation | null {
  if (!code) return null;
  const aiError = aiErrorPresentation(code, message ?? '');
  if (aiError) return { ...aiError, ...(message ? { technicalDetails: message } : {}) };
  if (code === 'IMAGE_SEARCH_APPAREL_UNCERTAIN') {
    return {
      title: '服装类目无法判断',
      description: message?.trim() || 'AI无法确定图片主要展示的商品类别，请使用主体清晰的图片重新导入，或手动重试本任务。'
    };
  }
  if (code === 'IMAGE_SEARCH_APPAREL_MODE_MISMATCH') {
    return {
      title: '服装模式不一致',
      description: message?.trim().replace('若认为AI误判，可手动重试本任务', '若认为AI误判，请换用主体明确的图片重新导入') || '服装勾选状态与图片中的售卖商品不一致，请调整勾选后重新导入图片；若认为AI误判，请换用主体明确的图片重新导入。'
    };
  }
  if (code === 'WB_CARD_CREATE_REJECTED') {
    const brand = message?.match(/(?:Бренд|Brand)\s*[«“"]([^»”"]+)[»”"]\s*(?:не найден|not found)/iu)?.[1];
    const count = message?.match(/rejected\s+(\d+)\s+card variant(?:\(s\))?/iu)?.[1];
    if (brand) {
      return {
        title: `WB 未找到品牌「${brand}」`,
        description: `品牌未被当前 WB 类目识别${count ? `，${count} 个 SKU 商品卡因此被拒绝。` : '，商品卡未创建。'}`,
        guidance: '若商品无品牌，请清空发布品牌或店铺默认品牌；若要使用该品牌，请换成 WB 收录的名称后重试。',
        ...(message ? { technicalDetails: message } : {})
      };
    }
    return {
      title: 'WB 拒绝创建商品卡',
      description: 'Wildberries 未接受商品卡请求。展开原始反馈查看具体原因。',
      ...(message ? { technicalDetails: message } : {})
    };
  }
  return {
    ...(jobErrors[code] ?? { title: '处理失败', description: '处理过程中出现异常，请查看任务详情或重试' }),
    ...(message ? { technicalDetails: message } : {})
  };
}
